import { ContentTypes, GetConversation } from '@/client/services/api/graphql/gql/graphql';
import { Link, ListItem, Stack, Text, UnorderedList } from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import ExpandToggleButton from './ExpandToggleButton';
import { useNavigateByContentType } from '@/client/services/hooks/navigation/useNavigateByContentType';
import { useTranslation } from 'react-i18next';

// Type-guard shape: the fields the renderer reads, all guaranteed truthy by the validReferences filter.
type RenderableReference = NonNullable<GetConversation['references'][number]> & {
  contentType: ContentTypes;
  contentId: string;
  contentTitle: string;
};

const COLLAPSED_LIMIT = 3;
// Hard cap on inline items; overflow beyond this is surfaced via the truncation hint, never silently dropped.
const EXPANDED_LIMIT = 10;

export default function ExchangeRelatedReferences({ relatedReferences }: { relatedReferences: GetConversation['references'] }) {
  const { t } = useTranslation();
  const { contentTypeMap } = useNavigateByContentType();
  const [isExpanded, setIsExpanded] = useState(false);

  // Memoised — this re-renders often during streaming; relatedReferences only changes with the payload.
  const validReferences = useMemo(
    () => relatedReferences.filter(
      (ref): ref is RenderableReference =>
        !!(ref?.contentType && ref.contentId && ref.contentTitle && contentTypeMap[ref.contentType]),
    ),
    // contentTypeMap omitted: useNavigateByContentType rebuilds it each render, but its contents are
    // static, so including it would defeat the memo and recompute the filter on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [relatedReferences],
  );

  if (!validReferences.length) return null;

  const totalValid = validReferences.length;
  const hasMore = totalValid > COLLAPSED_LIMIT;
  const shownCount = isExpanded ? Math.min(totalValid, EXPANDED_LIMIT) : Math.min(totalValid, COLLAPSED_LIMIT);
  const visibleReferences = validReferences.slice(0, shownCount);
  // Count the toggle actually reveals (capped at EXPANDED_LIMIT); the truncation hint discloses any overflow.
  const hiddenCount = Math.min(totalValid, EXPANDED_LIMIT) - COLLAPSED_LIMIT;
  const isTruncated = isExpanded && totalValid > EXPANDED_LIMIT;

  return (
    <Stack
      spacing={2}
      sx={{
        animation: 'fadeIn 0.5s ease-in-out',
        '@keyframes fadeIn': {
          '0%': { opacity: 0, transform: 'translateY(10px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      <Text fontSize="sm" fontWeight="medium" color="baseBlack">
        {t('aiAgent.relatedContent')}
      </Text>

      <UnorderedList spacing={1.5} pl={1}>
        {visibleReferences.map((reference) => (
          <ListItem
            key={`${reference.contentType}-${reference.contentId}`}
            // 13px/18px: no exact theme tokens (Chakra xs=12, sm=14); holding raw px pending design.
            fontSize="13px"
            lineHeight="18px"
            color="neutral.1300"
          >
            <Link
              as={RouterLink}
              to={`/${contentTypeMap[reference.contentType]}/${reference.contentId}`}
              color="neutral.1300"
              fontWeight="medium"
              textDecoration="underline"
              cursor="pointer"
              _hover={{ color: 'neutral.1300' }}
            >
              {reference.contentTitle}
            </Link>
          </ListItem>
        ))}
      </UnorderedList>

      {/* Toggles render outside the list so the expand/collapse affordances stay aligned. */}
      {hasMore && !isExpanded && (
        <ExpandToggleButton
          label={t('aiAgent.relatedContent.more', { count: hiddenCount })}
          onClick={() => setIsExpanded(true)}
        />
      )}

      {/* Truncation hint — only when EXPANDED_LIMIT actually clipped the list. */}
      {isTruncated && (
        <Text fontSize="13px" lineHeight="18px" color="neutral.700">
          {t('aiAgent.relatedContent.truncated', { shown: shownCount, total: totalValid })}
        </Text>
      )}

      {hasMore && isExpanded && (
        <ExpandToggleButton
          label={t('aiAgent.relatedContent.showLess')}
          onClick={() => setIsExpanded(false)}
        />
      )}
    </Stack>
  );
}
